import { useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import TuneIcon from '@mui/icons-material/Tune';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import { tokens } from '../../global/theme/tokens';
import { AiChatPermissionsSection } from './AiChatPermissions';

// null = the model's default; the rest is sent as reasoningEffort with every prompt
export const REASONING_EFFORTS = [
  { value: null, label: 'Default' },
  { value: 'none', label: 'Off' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const PERIOD_LABELS = {
  daily: 'Today',
  weekly: 'This week',
  session: 'Current session',
};

// share of the budget used from which the bar turns warning / error
// (error matches the panel's "almost used up" banner at 90 %)
const WARN_RATIO = 0.75;
const ERROR_RATIO = 0.9;

/**
 * Tune button in the chat header with a popover holding the chat options:
 * the budget usage, the thinking effort and the AI permissions.
 */
export const AiChatOptionsMenu = ({
  budget,
  permissions,
  canEditPermissions,
  reasoningEffort,
  onReasoningEffortChange,
  canChangeEffort,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState(null);

  return (
    <>
      <Tooltip title={t('Options')}>
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={t('Options')}
          sx={{
            position: 'relative',
            color: permissions.anyGranted ? colors.info : undefined,
          }}
        >
          <TuneIcon sx={{ fontSize: 20 }} />
          {permissions.anyGranted && (
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                right: 5,
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: colors.info,
              }}
            />
          )}
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: colors.glass,
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            width: 360,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 32px)',
          },
        }}
      >
        <UsageSection budget={budget} />
        <Divider sx={{ opacity: 0.5 }} />
        <EffortSection
          value={reasoningEffort}
          onChange={onReasoningEffortChange}
          disabled={!canChangeEffort}
        />
        <Divider sx={{ opacity: 0.5 }} />
        <AiChatPermissionsSection
          permissions={permissions}
          canEdit={canEditPermissions}
        />
      </Popover>
    </>
  );
};

const SectionTitle = ({ Icon, title, children }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box className="flex items-center gap-1.5">
      <Icon sx={{ fontSize: 18, color: colors.info }} />
      <Typography variant="subtitle2" fontWeight={600} className="flex-1">
        {title}
      </Typography>
      {children}
    </Box>
  );
};

/**
 * budget: undefined while unknown, null when the instance has no AI
 * budget, else { grant: { limitTokens, period }, remainingTokens, periodEndsAt }.
 */
const UsageSection = ({ budget }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const limit = budget?.grant?.limitTokens ?? 0;
  const available = budget && budget.remainingTokens !== null && limit > 0;
  const ratio = available
    ? Math.min(1, Math.max(0, 1 - budget.remainingTokens / limit))
    : 0;
  const percent = Math.round(ratio * 100);
  const color =
    ratio >= ERROR_RATIO
      ? colors.error
      : ratio >= WARN_RATIO
        ? colors.warning
        : colors.info;
  const periodLabel = PERIOD_LABELS[budget?.grant?.period];
  const resetsAt = budget?.periodEndsAt ? moment(budget.periodEndsAt) : null;

  return (
    <Box className="flex flex-col gap-1.5 px-3 pt-3 pb-3">
      <SectionTitle Icon={DataUsageIcon} title={t('Usage')}>
        {available && (
          <Typography variant="subtitle2" fontWeight={600} sx={{ color }}>
            {percent} %
          </Typography>
        )}
      </SectionTitle>
      {available ? (
        <>
          <LinearProgress
            variant="determinate"
            value={percent}
            aria-label={t('Usage')}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: colors.bgInput,
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
                borderRadius: 3,
              },
            }}
          />
          <Typography variant="caption" sx={{ opacity: 0.6, lineHeight: 1.4 }}>
            {t('{{percent}} % of the AI token budget used', { percent })}
          </Typography>
          {(periodLabel || resetsAt?.isValid()) && (
            <Typography variant="caption" sx={{ opacity: 0.6, lineHeight: 1.4 }}>
              {[
                periodLabel && t(periodLabel),
                resetsAt?.isValid() &&
                  t('Resets {{when}}', { when: resetsAt.fromNow() }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="caption" sx={{ opacity: 0.6, lineHeight: 1.4 }}>
          {budget === undefined
            ? t('Usage could not be loaded.')
            : t('No AI budget available.')}
        </Typography>
      )}
    </Box>
  );
};

const EffortSection = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();

  return (
    <Box className="flex flex-col gap-1.5 px-3 pt-3 pb-3">
      <SectionTitle Icon={PsychologyOutlinedIcon} title={t('Thinking')} />
      <Typography variant="caption" sx={{ opacity: 0.6, lineHeight: 1.4 }}>
        {t(
          'How much the AI thinks before it answers. More thinking helps with complex requests but uses more of the budget. Not every model supports every level.',
        )}
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        fullWidth
        // MUI treats a null value as "nothing selected", so the default is keyed by ''
        value={value ?? ''}
        onChange={(e, next) =>
          next !== null && onChange(next === '' ? null : next)
        }
        aria-label={t('Thinking')}
        sx={{
          mt: 0.5,
          '& .MuiToggleButton-root': { px: 0.5, py: 0.5, fontSize: '0.7rem' },
        }}
      >
        {REASONING_EFFORTS.map(({ value: effort, label }) => (
          <ToggleButton
            key={effort ?? 'default'}
            value={effort ?? ''}
            disabled={disabled}
          >
            {t(label)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
